/**
 * Hooks Test Page
 * Tests hooks with data flow visualizations:
 * - Kline: OHLC candlestick data
 * - Mark Price: Calculated mark price
 * - Last Price: Most recent trade price
 * - Bucket: Margin buckets
 * - Position: Trading positions
 * - Order: Pending orders
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useKline } from "../hooks/useKline";
import { useTradePrice } from "../hooks/useTradePrice";
import { useBuckets } from "../hooks/useBuckets";
import { usePositions } from "../hooks/usePositions";
import { usePendingOrders } from "../hooks/usePendingOrders";
import { useOrderbook } from "../hooks/useOrderbook";
import { useRates } from "../hooks/useRates";
import { useOnboard } from "../hooks/useOnboard";
import { useMarkets } from "../hooks/useMarkets";
import { useTradeOperation } from "../hooks/useTradeOperation";
import {
  SubscriptionProvider,
  useSubscription,
} from "../context/SubscriptionContext";
import {
  MultiFeedMessage,
  MultiFeedLastPriceData,
  isMultiFeedOrderbook,
  isMultiFeedTrades,
  isMultiFeedPositions,
  isMultiFeedOrders,
  isMultiFeedBuckets,
  isMultiFeedBalances,
  isMultiFeedMarkPrice,
  isMultiFeedFundingRate,
  isMultiFeedDeviation,
  isMultiFeedTWAP,
  isMultiFeedKline,
  isMultiFeedLastPrice,
  isMultiFeedIndexPrice,
  isMultiFeedPriceFeed,
  MultiFeedRaweEnvelope,
} from "../sdk/types/api";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { normalizeProtobufTimestamp } from "../sdk/utils/time";
import {
  formatPriceFromSatoshi,
  formatSatoshiPercent,
  formatNumber,
} from "../sdk/utils/display";
import { fromSatoshi } from "../sdk/utils/math";
import { LastPriceData } from "../sdk/types/data";
import {
  DEFAULT_BASE_URL,
  DEFAULT_WS_URL,
  DEFAULT_DEVICE_ID,
} from "../configs/base";
import { DEFAULT_MNEMONIC } from "../components/smartlayout/constants";
import {
  DataFlowVisualization,
  DataModal,
  DataRateHistogram,
  WebSocketToggleSection,
  SectionHeader,
  MultiFeedMessagesList,
  OrderOperationSection,
} from "./hook_test_page";
import "./hook_test_page/DataFlowVisualization.css";

// Constants
const SUBSCRIPTION_TIMEOUT_MS = 5000;
const MAX_ORDERBOOK_LEVELS_DISPLAY = 20;
const MAX_RECENT_MESSAGES_DISPLAY = 20;
const DEFAULT_KLINE_INTERVAL = "1m";
// Sub-channels that appear in the "Colored Messages" tab
const COLORED_SUB_CHANNELS = [
  "orderbook",
  "trades",
  "positions",
  "orders",
  "buckets",
  "balances",
];

/**
 * Inner component that uses the subscription context
 */
function HooksTestPageContent() {
  // Shared Configuration
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [wsURL, setWsURL] = useState(DEFAULT_WS_URL);
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);
  const [maxDataPoints, setMaxDataPoints] = useState<number>(100);
  const [maxVisible, setMaxVisible] = useState<number>(10);

  // Use onboard hook for wallet address and auth token
  const { walletAddress, authToken, error: onboardError } = useOnboard(baseURL);

  // Use markets hook for market selection
  const {
    markets,
    selectedMarketId,
    setSelectedMarketId,
    fetchMarkets,
    loadingMarkets,
    getMarket,
  } = useMarkets({
    baseURL,
    sessionToken: authToken,
    autoFetchMarkets: true,
  });

  // Kline Configuration
  const [klineMarketId, setKlineMarketId] = useState<string>("0");
  const [klineInterval, setKlineInterval] = useState<string>("1m");

  // Trade Price Configuration
  const [tradePriceMarketId, setTradePriceMarketId] = useState<string>("0");
  const [tradePriceSymbol, setTradePriceSymbol] = useState<string>("BTC/USDC");

  // Orderbook Configuration
  const [orderbookMarketId, setOrderbookMarketId] = useState<string>("0");

  // Funding Rate Configuration (single market ID)
  const [fundingRateMarketId, setFundingRateMarketId] = useState<string>("0");

  // Order Placement Configuration
  const [orderPlacementMnemonic, setOrderPlacementMnemonic] = useState<string>(DEFAULT_MNEMONIC);
  const [orderPlacementBucketId, setOrderPlacementBucketId] = useState<string>("");
  const [orderPlacementSide, setOrderPlacementSide] = useState<"buy" | "sell">("buy");
  const [orderPlacementOrderType, setOrderPlacementOrderType] = useState<"market" | "limit">("market");
  const [orderPlacementPrice, setOrderPlacementPrice] = useState<number>(0);
  const [orderPlacementQuantity, setOrderPlacementQuantity] = useState<number>(0);
  const [orderPlacementLeverage, setOrderPlacementLeverage] = useState<number>(1);

  // Update market IDs when selectedMarketId changes
  useEffect(() => {
    if (selectedMarketId) {
      const marketIdStr = String(selectedMarketId);
      setKlineMarketId(marketIdStr);
      setTradePriceMarketId(marketIdStr);
      setOrderbookMarketId(marketIdStr);
      setFundingRateMarketId(marketIdStr);

      // Clear multi-feed messages when market changes (memory management)
      setRecentMessages([]);
      setColoredMessages([]);
      setMultiFeedSubChannelCounts(new Map());

      // Also update symbol if market is found
      // Convert selectedMarketId to number if it's a string
      const marketIdNum =selectedMarketId || ""
        const market = getMarket(marketIdNum);
        if (market) {
          setTradePriceSymbol(
            market.symbol || `${market.baseAsset}/${market.quoteAsset}`,
          );
      }
    }
  }, [selectedMarketId, getMarket]);

  // Modal State
  const [modalData, setModalData] = useState<unknown>(null);
  const [modalTitle, setModalTitle] = useState<string>("Data Details");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Use subscription context (must be called before hooks that use client)
  const {
    client,
    isEnabled,
    isConnected: subscriptionConnected,
    connectionState,
    toggleEnabled,
    getRetainedData,
    getAllRetainedData,
    wsURL: contextWsURL,
    setWsURL: setContextWsURL,
    authToken: contextAuthToken,
    setAuthToken: setContextAuthToken,
    walletAddress: contextWalletAddress,
    setWalletAddress: setContextWalletAddress,
    deviceID: contextDeviceID,
    setDeviceID: setContextDeviceID,
    subscribeMultiFeedWithRetention,
    unsubscribeMultiFeed,
  } = useSubscription();

  // Hooks
  const {
    klineData,
    loading: klineLoading,
    error: klineError,
    isConnected: klineConnected,
  } = useKline({
    subscriptionClient: client, // Use client from SubscriptionContext
    marketId: klineMarketId,
    interval: klineInterval,
    maxDataPoints,
  });

  // Sync context with local state (consolidated to prevent infinite loops)
  useEffect(() => {
    // Use refs to track if we're updating to prevent circular updates
    const updates: Array<() => void> = [];

    if (wsURL !== contextWsURL) {
      updates.push(() => setContextWsURL(wsURL));
    }
    if (authToken !== contextAuthToken) {
      updates.push(() => setContextAuthToken(authToken));
    }
    if (walletAddress !== contextWalletAddress) {
      updates.push(() => setContextWalletAddress(walletAddress));
    }
    if (deviceID !== contextDeviceID) {
      updates.push(() => setContextDeviceID(deviceID));
    }

    // Apply all updates in a single batch
    updates.forEach((update) => update());
  }, [
    wsURL,
    contextWsURL,
    setContextWsURL,
    authToken,
    contextAuthToken,
    setContextAuthToken,
    walletAddress,
    contextWalletAddress,
    setContextWalletAddress,
    deviceID,
    contextDeviceID,
    setContextDeviceID,
  ]);

  // Multi-Feed State (declare early so it can be used by other hooks)
  const [recentMessages, setRecentMessages] = useState<MultiFeedMessage[]>([]);
  const [coloredMessages, setColoredMessages] = useState<MultiFeedMessage[]>(
    [],
  );
  const [multiFeedConnected, setMultiFeedConnected] = useState<boolean>(false);
  const [multiFeedSubChannelCounts, setMultiFeedSubChannelCounts] = useState<
    Map<string, number>
  >(new Map());
  const [activeMultiFeedTab, setActiveMultiFeedTab] = useState<
    "recent" | "colored"
  >("recent");

  // Last Price state (from multi-feed)
  const [lastPrice, setLastPrice] = useState<LastPriceData | null>(null);
  const [lastPriceLoading, setLastPriceLoading] = useState<boolean>(true);
  const [lastPriceError, setLastPriceError] = useState<string | null>(null);

  // Mark Price state (still using useTradePrice for now, can be migrated later)
  const {
    markPrice,
    loading: markPriceLoading,
    error: markPriceError,
    isConnected: markPriceConnected,
  } = useTradePrice({
    subscriptionClient: client,
    baseURL,
    marketId: tradePriceMarketId,
    // Disable polling fallback when WebSocket is disconnected
    disablePolling: true,
  });

  // Trade price connected state (use multi-feed connection for last_price)
  const tradePriceConnected = multiFeedConnected;
  // Accumulate last price data points
  type HistoryDataPoint = {
    value: number;
    timestamp: number;
    arrivalTime: number;
  };
  const [lastPriceHistory, setLastPriceHistory] = useState<HistoryDataPoint[]>(
    [],
  );
  const [markPriceHistory, setMarkPriceHistory] = useState<HistoryDataPoint[]>(
    [],
  );

  // Update last price history when new data arrives
  useEffect(() => {
    if (lastPrice) {
      setLastPriceHistory((prev) => {
        const updated = [
          ...prev,
          {
            ...lastPrice,
            value: lastPrice.price,
            timestamp: lastPrice.timestamp,
            arrivalTime: Date.now(), // Track when data arrived
          },
        ];
        // Keep only the most recent maxDataPoints
        if (updated.length > maxDataPoints) {
          return updated.slice(-maxDataPoints);
        }
        return updated;
      });
    }
  }, [lastPrice, maxDataPoints]);

  // Update mark price history when new data arrives
  useEffect(() => {
    if (markPrice) {
      setMarkPriceHistory((prev) => {
        const updated = [
          ...prev,
          {
            ...markPrice,
            value: markPrice.mark_price,
            timestamp: markPrice.timestamp,
            arrivalTime: Date.now(), // Track when data arrived
          },
        ];
        // Keep only the most recent maxDataPoints
        if (updated.length > maxDataPoints) {
          return updated.slice(-maxDataPoints);
        }
        return updated;
      });
    }
  }, [markPrice, maxDataPoints]);

  const {
    buckets,
    loading: bucketsLoading,
    error: bucketsError,
    refresh: refreshBuckets,
  } = useBuckets({
    subscriptionClient: client, // Use client from SubscriptionContext
    baseURL,
    authToken,
    walletAddress,
    // Disable polling - rely on WebSocket multi-feed for real-time updates
    refreshInterval: 0,
    useWebSocket: true, // Use WebSocket as primary source
    marketId: "0", // User-global market for buckets
  });

  const {
    positions,
    loading: positionsLoading,
    error: positionsError,
    isConnected: positionsConnected,
    refresh: refreshPositions,
  } = usePositions({
    subscriptionClient: client, // Use client from SubscriptionContext
    baseURL,
    authToken,
    walletAddress,
    useWebSocket: true,
    // Disable polling fallback when WebSocket is disconnected
    refreshInterval: 0,
  });

  const {
    orders,
    loading: ordersLoading,
    error: ordersError,
    isConnected: ordersConnected,
    refresh: refreshOrders,
  } = usePendingOrders({
    subscriptionClient: client, // Use client from SubscriptionContext
    baseURL,
    authToken,
    walletAddress,
    deviceID,
    useWebSocket: true,
    // Disable polling fallback when WebSocket is disconnected
    refreshInterval: 0,
  });

  const {
    asks: orderbookAsks,
    bids: orderbookBids,
    spread: orderbookSpread,
    midPrice: orderbookMidPrice,
    market_id: orderbookMarketIdFromHook,
    loading: orderbookLoading,
    error: orderbookError,
    isConnected: orderbookConnected,
    refresh: refreshOrderbook,
  } = useOrderbook({
    subscriptionClient: client, // Use client from SubscriptionContext
    baseURL,
    marketId: orderbookMarketId,
    // Disable polling fallback when WebSocket is disconnected
    disablePolling: true,
  });

  const {
    latestFundingRate,
    latestDeviation,
    latestTWAP,
    loading: fundingRateLoading,
    error: fundingRateError,
    isConnected: fundingRateConnected,
  } = useRates({
    subscriptionClient: client, // Use client from SubscriptionContext
    baseURL,
    marketId: fundingRateMarketId,
  });

  // Order Placement Hook

  const {
    orderType: tradeOpOrderType,
    setOrderType: setTradeOpOrderType,
    sizeInput: tradeOpSizeInput,
    setSizeInput: setTradeOpSizeInput,
    price: tradeOpPrice,
    setPrice: setTradeOpPrice,
    leverage: tradeOpLeverage,
    setLeverage: setTradeOpLeverage,
    isReady: tradeOpIsReady,
    loading: tradeOpLoading,
    result: tradeOpResult,
    placeBuyOrder,
    placeSellOrder,
    getOrderPrice,
    calculateSize,
  } = useTradeOperation({
    baseURL,
    mnemonic: orderPlacementMnemonic,
    marketId: selectedMarketId ? String(selectedMarketId) : "0",
    selectedBucketId: orderPlacementBucketId,
    tradePrice: markPrice?.mark_price || lastPrice?.price || undefined,
  });

  // Memoize formatted rates data (funding rate, deviation, TWAP) to avoid recalculating on every render
  const formattedRates = useMemo(() => {
    const hasAnyData = latestFundingRate || latestDeviation || latestTWAP;
    if (!hasAnyData) return null;

    return {
      marketId: fundingRateMarketId,
      // Funding Rate data
      fundingRate: latestFundingRate
        ? {
            latest: latestFundingRate,
            fundingRate: formatSatoshiPercent(
              typeof latestFundingRate.funding_rate === "string"
                ? latestFundingRate.funding_rate
                : latestFundingRate.funding_rate,
            ),
            markPrice: formatPriceFromSatoshi(
              typeof latestFundingRate.mark_price === "string"
                ? latestFundingRate.mark_price
                : latestFundingRate.mark_price,
            ),
            indexPrice: formatPriceFromSatoshi(
              typeof latestFundingRate.index_price === "string"
                ? latestFundingRate.index_price
                : latestFundingRate.index_price,
            ),
            premiumRate: formatSatoshiPercent(
              typeof latestFundingRate.premium_rate === "string"
                ? latestFundingRate.premium_rate
                : latestFundingRate.premium_rate,
            ),
            symbol: latestFundingRate.symbol || "N/A",
            formattedTimestamp: latestFundingRate.timestamp
              ? new Date(
                  normalizeProtobufTimestamp(latestFundingRate.timestamp),
                ).toLocaleString()
              : null,
          }
        : null,
      // Deviation data
      deviation: latestDeviation
        ? {
            latest: latestDeviation,
            deviationBps: latestDeviation.deviation_bps,
            deviationPercent: formatSatoshiPercent(latestDeviation.deviation_percent   ),
            lastPrice: formatPriceFromSatoshi(latestDeviation.last_price  ),
            markPrice: formatPriceFromSatoshi(latestDeviation.mark_price    ),
            indexPrice: formatPriceFromSatoshi(latestDeviation.index_price     ),
            direction: latestDeviation.direction || "N/A",
            symbol: latestDeviation.symbol || "N/A",
            formattedTimestamp: latestDeviation.timestamp
              ? new Date(
                  normalizeProtobufTimestamp(latestDeviation.timestamp),
                ).toLocaleString()
              : null,
          }
        : null,
      // TWAP data
      twap: latestTWAP
        ? {
            latest: latestTWAP,
            twapValue: formatPriceFromSatoshi(
              typeof latestTWAP.twap_value === "string"
                ? latestTWAP.twap_value
                : latestTWAP.twap_value,
            ),
            windowSize: latestTWAP.window_size?.toString() || "N/A",
            sampleCount: latestTWAP.sample_count?.toString() || "N/A",
            calculationMethod: latestTWAP.calculation_method || "N/A",
            startTime: latestTWAP.start_time
              ? new Date(latestTWAP.start_time * 1000).toLocaleString()
              : "N/A",
            endTime: latestTWAP.end_time
              ? new Date(latestTWAP.end_time * 1000).toLocaleString()
              : "N/A",
            symbol: latestTWAP.symbol || "N/A",
          }
        : null,
    };
  }, [
    fundingRateMarketId,
    latestFundingRate,
    latestDeviation,
    latestTWAP,
  ]);

  // Multi-Feed computed values (memoized to prevent unnecessary recalculations)
  const multiFeedMarketId = useMemo(() => {
    return selectedMarketId ? String(selectedMarketId) : orderbookMarketId;
  }, [selectedMarketId, orderbookMarketId]);

  const multiFeedSymbol = useMemo(() => {
    return tradePriceSymbol || "BTC/USDC";
  }, [tradePriceSymbol]);

  // Multi-Feed Handler (with proper dependencies)
  const handleMultiFeedMessage = useCallback((message: MultiFeedMessage) => {
    // Route message to the correct list based on COLORED_SUB_CHANNELS
    if (COLORED_SUB_CHANNELS.includes(message.sub_channel)) {
      setColoredMessages((prev) => {
        const updated = [...prev, message];
        // Keep only last 100 messages (stored in chronological order)
        return updated.slice(-100);
      });
    } else {
      setRecentMessages((prev) => {
        const updated = [...prev, message];
        // Keep only last 20 messages (stored in chronological order)
        return updated.slice(-20);
      });
    }

    // Update sub-channel counts
    setMultiFeedSubChannelCounts((prev) => {
      const updated = new Map(prev);
      const count = updated.get(message.sub_channel) || 0;
      updated.set(message.sub_channel, count + 1);
      return updated;
    });

    // Handle last_price updates from multi-feed
    if (isMultiFeedLastPrice(message)) {
      try {
        const lastPriceData: MultiFeedLastPriceData = message.data;
        // Transform MultiFeedLastPriceData to LastPriceData format
        const transformedLastPrice: LastPriceData = {
          market_id: lastPriceData.market_id,
          price: toNumberFromSatoshi(lastPriceData.price),
          quantity: toNumberFromSatoshi(lastPriceData.quantity),
          timestamp: normalizeProtobufTimestamp(lastPriceData.timestamp),
          is_buyer_maker: lastPriceData.is_buyer_maker,
          trade_id: lastPriceData.trade_id,
        };
        setLastPrice(transformedLastPrice);
        setLastPriceLoading(false);
        setLastPriceError(null);
      } catch (error) {
        console.error(
          "[HooksTestPage] Error processing last_price from multi-feed:",
          error,
        );
        setLastPriceError(
          error instanceof Error
            ? error.message
            : "Failed to process last_price",
        );
      }
    }
  }, []); // State setters are stable, no dependencies needed

  // Sync multi-feed connection state with context
  useEffect(() => {
    setMultiFeedConnected(subscriptionConnected && isEnabled);
  }, [subscriptionConnected, isEnabled]);

  // Multi-Feed Subscription using context (optimized with memoized interval)
  const subscriptionInterval = useMemo(
    () => klineInterval || DEFAULT_KLINE_INTERVAL,
    [klineInterval],
  );

  useEffect(() => {
    if (!multiFeedMarketId || !multiFeedSymbol || !client || !isEnabled) {
      // If disabled, clear messages but keep retained data
      if (!isEnabled) {
        setRecentMessages([]);
        setColoredMessages([]);
        setMultiFeedSubChannelCounts(new Map());
      }
      return;
    }

    // Only subscribe if client is connected
    if (!subscriptionConnected) {
      console.debug("[HooksTestPage] Client not connected yet, waiting...");
      return;
    }

    // Load retained data BEFORE subscribing (fix race condition)
    const retained = getRetainedData("last_price");
    if (retained && isMultiFeedLastPrice(retained)) {
      try {
        const lastPriceData: LastPriceData = {
          market_id: retained.data.market_id,
          price: toNumberFromSatoshi(retained.data.price),
          quantity: toNumberFromSatoshi(retained.data.quantity),
          timestamp: normalizeProtobufTimestamp(retained.data.timestamp),
          is_buyer_maker: retained.data.is_buyer_maker,
          trade_id: retained.data.trade_id,
        };
        setLastPrice(lastPriceData);
        setLastPriceLoading(false);
        setLastPriceError(null);
      } catch (error) {
        console.error(
          "[HooksTestPage] Error processing retained last_price:",
          error,
        );
        setLastPriceError(
          error instanceof Error
            ? error.message
            : "Failed to process retained last_price",
        );
      }
    }

    // Subscribe to multi-feed using context's retained data wrapper
    console.debug("[HooksTestPage] Subscribing to multi-feed with:", {
      marketId: multiFeedMarketId,
      symbol: multiFeedSymbol,
      interval: subscriptionInterval,
    });

    let subscriptionAcknowledged = false;

    try {
      subscribeMultiFeedWithRetention(
        multiFeedMarketId,
        multiFeedSymbol,
        handleMultiFeedMessage,
        subscriptionInterval,
      );

      // Wait for subscription acknowledgment
      if (client) {
        const timeoutId = setTimeout(() => {
          if (!subscriptionAcknowledged) {
            console.warn(
              "[HooksTestPage] Multi-feed subscription acknowledgment timeout",
            );
            setLastPriceError("Multi-feed subscription acknowledgment timeout");
            setLastPriceLoading(false);
          }
        }, SUBSCRIPTION_TIMEOUT_MS);

        client
          .waitForSubscription("multi_feed", SUBSCRIPTION_TIMEOUT_MS)
          .then(() => {
            subscriptionAcknowledged = true;
            clearTimeout(timeoutId);
            console.debug(
              "[HooksTestPage] Multi-feed subscription acknowledged by server",
            );
            setLastPriceLoading(false);
            setLastPriceError(null);
          })
          .catch((error) => {
            subscriptionAcknowledged = true;
            clearTimeout(timeoutId);
            console.warn(
              "[HooksTestPage] Multi-feed subscription acknowledgment error:",
              error,
            );
            setLastPriceError("Multi-feed subscription acknowledgment failed");
            setLastPriceLoading(false);
          });
      }
    } catch (error) {
      console.error(
        "[HooksTestPage] Failed to subscribe to multi-feed:",
        error,
      );
      setLastPriceError(
        error instanceof Error ? error.message : "Failed to subscribe",
      );
      setLastPriceLoading(false);
    }

    return () => {
      if (client && multiFeedMarketId && multiFeedSymbol) {
        try {
          unsubscribeMultiFeed(
            multiFeedMarketId,
            multiFeedSymbol,
            handleMultiFeedMessage,
          );
        } catch (error) {
          console.error(
            "[HooksTestPage] Error unsubscribing from multi-feed:",
            error,
          );
        }
      }
    };
  }, [
    multiFeedMarketId,
    multiFeedSymbol,
    client,
    isEnabled,
    subscriptionConnected,
    subscriptionInterval,
    handleMultiFeedMessage,
    subscribeMultiFeedWithRetention,
    unsubscribeMultiFeed,
    getRetainedData,
  ]);

  // Handle modal open
  // Regular function is fine here since:
  // - It's wrapped in arrow functions when passed to children anyway: (item) => handleItemClick(item, "Title")
  // - The child components aren't memoized, so stable references don't prevent re-renders
  // - State setters are stable, so the function behavior is consistent
  const handleItemClick = (
    item: { raw?: any; message?: any; data?: any },
    title: string,
  ) => {
    const data = item.raw || item.message || item.data || item;
    setModalData(data);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  // Handle modal close - memoized because DataModal has it in a useEffect dependency array
  // Note: This is NOT a pure function (it has side effects - modifies state), which is expected for event handlers
  // useCallback is needed here to provide a stable reference so the useEffect in DataModal doesn't re-run unnecessarily
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []); // setIsModalOpen is stable, no dependencies needed

  // Sync order placement state with hook state
  useEffect(() => {
    setTradeOpOrderType(orderPlacementOrderType);
  }, [orderPlacementOrderType, setTradeOpOrderType]);

  useEffect(() => {
    setTradeOpSizeInput(orderPlacementQuantity);
  }, [orderPlacementQuantity, setTradeOpSizeInput]);

  useEffect(() => {
    setTradeOpPrice(orderPlacementPrice);
  }, [orderPlacementPrice, setTradeOpPrice]);

  useEffect(() => {
    setTradeOpLeverage(orderPlacementLeverage);
  }, [orderPlacementLeverage, setTradeOpLeverage]);

  // Handle order placement
  const handlePlaceOrder = useCallback(async () => {
    if (orderPlacementSide === "buy") {
      await placeBuyOrder();
    } else {
      await placeSellOrder();
    }
  }, [orderPlacementSide, placeBuyOrder, placeSellOrder]);

  // Randomize order parameters
  const handleRandomize = useCallback(() => {
    // Random bucket ID from available buckets
    if (buckets.length > 0) {
      const randomBucket = buckets[Math.floor(Math.random() * buckets.length)];
      setOrderPlacementBucketId(randomBucket.bucketId);
    }

    // Random side (buy or sell)
    const randomSide = Math.random() < 0.5 ? "buy" : "sell";
    setOrderPlacementSide(randomSide);

    // Random order type (market or limit)
    const randomOrderType = Math.random() < 0.5 ? "market" : "limit";
    setOrderPlacementOrderType(randomOrderType);

    // Random price (10000-90000, but 0 if market order)
    if (randomOrderType === "market") {
      setOrderPlacementPrice(0);
    } else {
      const randomPrice = Math.floor(Math.random() * (90000 - 10000 + 1)) + 10000;
      setOrderPlacementPrice(randomPrice);
    }

    // Random quantity (0.01 - 1.5)
    const randomQuantity = Math.random() * (1.5 - 0.01) + 0.01;
    setOrderPlacementQuantity(parseFloat(randomQuantity.toFixed(3)));

    // Random leverage (1-10)
    const randomLeverage = Math.floor(Math.random() * 10) + 1;
    setOrderPlacementLeverage(randomLeverage);
  }, [buckets]);


  return (
    <div className="App-content-row">
      <h1>Hooks Test QA</h1>
      <p>realtime monitor of data flow and block</p>
      {/* WebSocket Toggle */}
      <WebSocketToggleSection
        isEnabled={isEnabled}
        isConnected={subscriptionConnected}
        connectionState={connectionState}
        retainedDataCount={getAllRetainedData().size}
        onToggle={toggleEnabled}
      />

      {/* Configuration Section */}
      <div className="App-section">
        <h2 className="hooks-test-section-header">Shared Configuration</h2>
        <div className="hooks-test-config-grid">
          <div>
            <label className="hooks-test-label">
              API Base URL:
              <input
                type="text"
                className="hooks-test-input"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              WebSocket URL:
              <input
                type="text"
                className="hooks-test-input"
                value={wsURL}
                onChange={(e) => setWsURL(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Wallet Address (from useOnboard):
              <input
                type="text"
                className="hooks-test-input-readonly"
                value={walletAddress || ""}
                readOnly
                placeholder="Set via useOnboard hook"
              />
              {onboardError && (
                <div className="hooks-test-error-message">{onboardError}</div>
              )}
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Auth Token (from useOnboard):
              <input
                type="text"
                className="hooks-test-input-readonly"
                value={authToken || ""}
                readOnly
                placeholder="Set via useOnboard hook"
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Market Selection:
              <select
                className="hooks-test-select"
                value={selectedMarketId || ""}
                onChange={(e) => {
                  const marketId = e.target.value;
                  if (marketId) {
                    setSelectedMarketId(marketId);
                  }
                }}
              >
                <option value="">Select a market...</option>
                {markets.map((market) => (
                  <option
                    key={market.market_id}
                    value={String(market.market_id)}
                  >
                    {market.symbol ||
                      `${market.baseAsset}/${market.quoteAsset}`}{" "}
                    (ID: {market.market_id})
                  </option>
                ))}
              </select>
              {loadingMarkets && (
                <div className="hooks-test-info-message">
                  Loading markets...
                </div>
              )}
              {!loadingMarkets && markets.length === 0 && (
                <button
                  className="hooks-test-button hooks-test-button-with-margin"
                  onClick={() => fetchMarkets()}
                >
                  Fetch Markets
                </button>
              )}
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Device ID:
              <input
                type="text"
                className="hooks-test-input"
                value={deviceID}
                onChange={(e) => setDeviceID(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Max Data Points (Memory):
              <input
                type="number"
                className="hooks-test-input"
                value={maxDataPoints}
                onChange={(e) =>
                  setMaxDataPoints(
                    Math.max(10, parseInt(e.target.value) || 100),
                  )
                }
                min="10"
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Max Visible Rectangles:
              <input
                type="number"
                className="hooks-test-input"
                value={maxVisible}
                onChange={(e) =>
                  setMaxVisible(Math.max(1, parseInt(e.target.value) || 10))
                }
                min="1"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Kline Section */}
      <div className="App-section">
        <SectionHeader
          title="Kline Hook"
          isConnected={klineConnected}
          statusBadges={[
            <span
              key="data-points"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {klineData.length} data points
            </span>,
          ]}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (controlled by Market Selection above):
              <input
                type="text"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
                value={klineMarketId}
                onChange={(e) => setKlineMarketId(e.target.value)}
                placeholder="Select market from dropdown above"
              />
              <div className="hooks-test-helper-text">
                Can be manually overridden if needed
              </div>
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Interval:
              <select
                className="hooks-test-select"
                value={klineInterval}
                onChange={(e) => setKlineInterval(e.target.value)}
              >
                <option value="1m">1m</option>
                <option value="5m">5m</option>
                <option value="15m">15m</option>
                <option value="1h">1h</option>
                <option value="4h">4h</option>
                <option value="1d">1d</option>
              </select>
            </label>
          </div>
        </div>

        {klineError && (
          <div className="hooks-test-error-box">Error: {klineError}</div>
        )}

        {klineLoading && klineData.length === 0 ? (
          <div className="hooks-test-loading-state">Loading kline data...</div>
        ) : klineData.length === 0 ? (
          <div className="hooks-test-empty-state">No kline data available</div>
        ) : (
          <>
            <DataFlowVisualization
              data={klineData}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.close || item.high || 100}
              onItemClick={(item) => handleItemClick(item, "Kline Data")}
            />
            <DataRateHistogram
              data={klineData}
              getTimestamp={(item) => {
                // Kline data has timestamp and open_time fields
                // Ensure timestamp is in milliseconds (convert if in seconds)
                let timestamp = item.timestamp || item.open_time || Date.now();

                // If timestamp is less than a year 2000 timestamp in milliseconds,
                // it's likely in seconds, so convert to milliseconds
                if (timestamp < 946684800000) {
                  timestamp = timestamp * 1000;
                }

                return timestamp;
              }}
            />
          </>
        )}
      </div>

      {/* Mark Price Section */}
      <div className="App-section">
        <SectionHeader
          title="Mark Price Hook"
          isConnected={tradePriceConnected}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (controlled by Market Selection above):
              <input
                type="text"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
                value={tradePriceMarketId}
                onChange={(e) => setTradePriceMarketId(e.target.value)}
                placeholder="Select market from dropdown above"
              />
              <div className="hooks-test-helper-text">
                Mark price subscription uses marketId (not symbol). Can be
                manually overridden if needed.
              </div>
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Symbol (for display only, auto-updated from selected market):
              <input
                type="text"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
                value={tradePriceSymbol}
                onChange={(e) => setTradePriceSymbol(e.target.value)}
                placeholder="BTC/USDC"
              />
              <div className="hooks-test-helper-text">
                Symbol is only used for display in mark price data, not for
                subscription.
              </div>
            </label>
          </div>
        </div>

        {markPriceError && (
          <div className="hooks-test-error-box">Error: {markPriceError}</div>
        )}

        {markPriceHistory.length === 0 ? (
          <div className="hooks-test-loading-state">
            No mark price data available
          </div>
        ) : (
          <>
            <DataFlowVisualization
              data={markPriceHistory}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.value || item.mark_price || 100}
              onItemClick={(item) => handleItemClick(item, "Mark Price Data")}
            />
            <DataRateHistogram
              data={markPriceHistory}
              getTimestamp={(item) =>
                item.arrivalTime || item.timestamp || Date.now()
              }
            />
          </>
        )}
      </div>

      {/* Last Price Section */}
      <div className="App-section">
        <SectionHeader
          title="Last Price Hook"
          isConnected={tradePriceConnected}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (controlled by Market Selection above):
              <input
                type="text"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
                value={tradePriceMarketId}
                onChange={(e) => setTradePriceMarketId(e.target.value)}
                placeholder="Select market from dropdown above"
              />
              <div className="hooks-test-helper-text">
                Can be manually overridden if needed
              </div>
            </label>
          </div>
        </div>

        {lastPriceError && (
          <div className="hooks-test-error-box">Error: {lastPriceError}</div>
        )}

        {lastPriceLoading && lastPriceHistory.length === 0 ? (
          <div className="hooks-test-loading-state">
            Loading last price data...
          </div>
        ) : lastPriceHistory.length === 0 ? (
          <div className="hooks-test-loading-state">
            No last price data available
          </div>
        ) : (
          <>
            <DataFlowVisualization
              data={lastPriceHistory}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.value || item.price || 100}
              onItemClick={(item) => handleItemClick(item, "Last Price Data")}
            />
            <DataRateHistogram
              data={lastPriceHistory}
              getTimestamp={(item) =>
                item.arrivalTime || item.timestamp || Date.now()
              }
            />
          </>
        )}
      </div>

      {/* Bucket Section */}
      <div className="App-section">
        <SectionHeader
          title="Bucket Hook (Addressed)"
          statusBadges={[
            <span
              key="buckets-count"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {buckets.length} buckets
            </span>,
          ]}
          actions={[
            <button
              key="refresh"
              className="hooks-test-button"
              onClick={() => refreshBuckets()}
              disabled={bucketsLoading}
            >
              {bucketsLoading ? "Loading..." : "Refresh"}
            </button>,
          ]}
        />

        {bucketsError && (
          <div className="hooks-test-error-box">Error: {bucketsError}</div>
        )}

        {bucketsLoading && buckets.length === 0 ? (
          <div className="hooks-test-loading-state">Loading buckets...</div>
        ) : buckets.length === 0 ? (
          <div className="hooks-test-loading-state">No buckets found</div>
        ) : (
          <>
            <DataFlowVisualization
              data={buckets}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.totalMargin || item.unrealizedPnL || 100}
              onItemClick={(item) => handleItemClick(item, "Bucket Data")}
            />
            <DataRateHistogram
              data={buckets}
              getTimestamp={(item) => {
                // For buckets, use createdAt or current time
                return item.createdAt
                  ? new Date(item.createdAt).getTime()
                  : Date.now();
              }}
            />
          </>
        )}
      </div>

      {/* Position Section */}
      <div className="App-section">
        <SectionHeader
          title="Position Hook (Addressed)"
          isConnected={positionsConnected}
          statusBadges={[
            <span
              key="positions-count"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {positions.length} positions
            </span>,
          ]}
          actions={[
            <button
              key="refresh"
              className="hooks-test-button"
              onClick={() => refreshPositions()}
              disabled={positionsLoading}
            >
              {positionsLoading ? "Loading..." : "Refresh"}
            </button>,
          ]}
        />

        {positionsError && (
          <div className="hooks-test-error-box">Error: {positionsError}</div>
        )}

        {positionsLoading && positions.length === 0 ? (
          <div className="hooks-test-loading-state">Loading positions...</div>
        ) : positions.length === 0 ? (
          <div className="hooks-test-loading-state">No positions found</div>
        ) : (
          <>
            <DataFlowVisualization
              data={positions}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.size || item.margin || 100}
              onItemClick={(item) => handleItemClick(item, "Position Data")}
            />
            <DataRateHistogram
              data={positions}
              getTimestamp={(item) => {
                // Positions might not have timestamps, use current time
                // In a real scenario, positions would have update timestamps
                return Date.now();
              }}
            />
          </>
        )}
      </div>

      {/* Order Section */}
      <div className="App-section">
        <SectionHeader
          title="Order Hook (Addressed)"
          isConnected={ordersConnected}
          statusBadges={[
            <span
              key="orders-count"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {orders.length} orders
            </span>,
          ]}
          actions={[
            <button
              key="refresh"
              className="hooks-test-button"
              onClick={() => refreshOrders()}
              disabled={ordersLoading}
            >
              {ordersLoading ? "Loading..." : "Refresh"}
            </button>,
          ]}
        />

        {ordersError && (
          <div className="hooks-test-error-box">Error: {ordersError}</div>
        )}

        {ordersLoading && orders.length === 0 ? (
          <div className="hooks-test-loading-state">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="hooks-test-loading-state">No orders found</div>
        ) : (
          <>
            <DataFlowVisualization
              data={orders}
              maxVisible={maxVisible}
              maxDataPoints={maxDataPoints}
              getValue={(item) => item.quantity || item.price || 100}
              onItemClick={(item) => handleItemClick(item, "Order Data")}
            />
            <DataRateHistogram
              data={orders}
              getTimestamp={(item) => {
                // Use createdAt, updatedAt, or current time
                if (item.createdAt)
                  return typeof item.createdAt === "number"
                    ? item.createdAt
                    : new Date(item.createdAt).getTime();
                if (item.updatedAt)
                  return typeof item.updatedAt === "number"
                    ? item.updatedAt
                    : new Date(item.updatedAt).getTime();
                return Date.now();
              }}
            />
          </>
        )}
      </div>

      {/* Orderbook Section */}
      <div className="App-section">
        <SectionHeader
          title="Orderbook Hook"
          isConnected={orderbookConnected}
          statusBadges={[
            <span
              key="levels-count"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {orderbookBids.length + orderbookAsks.length} levels
            </span>,
          ]}
          actions={[
            <button
              key="refresh"
              className="hooks-test-button"
              onClick={() => refreshOrderbook()}
              disabled={orderbookLoading}
            >
              {orderbookLoading ? "Loading..." : "Refresh"}
            </button>,
          ]}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (controlled by Market Selection above):
              <input
                type="text"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
                value={orderbookMarketId}
                onChange={(e) => setOrderbookMarketId(e.target.value)}
                placeholder="Select market from dropdown above"
              />
              <div className="hooks-test-helper-text">
                Can be manually overridden if needed
              </div>
            </label>
          </div>
        </div>

        {orderbookError && (
          <div className="hooks-test-error-box">Error: {orderbookError}</div>
        )}

        {orderbookLoading &&
        orderbookBids.length === 0 &&
        orderbookAsks.length === 0 ? (
          <div className="hooks-test-loading-state">Loading orderbook...</div>
        ) : orderbookBids.length === 0 && orderbookAsks.length === 0 ? (
          <div className="hooks-test-empty-state">
            No orderbook data available
          </div>
        ) : (
          <div className="hooks-test-orderbook-container">
            <div className="hooks-test-orderbook-grid">
              <div>
                <h3 className="hooks-test-orderbook-section-title hooks-test-orderbook-section-title-bids">
                  Bids ({orderbookBids.length})
                </h3>
                <div className="hooks-test-orderbook-levels-container">
                  {orderbookBids
                    .slice(0, MAX_ORDERBOOK_LEVELS_DISPLAY)
                    .map((bid, idx) => (
                      <div
                        key={idx}
                        className={`hooks-test-orderbook-level-row ${
                          idx % 2 === 0
                            ? "hooks-test-orderbook-level-row-even"
                            : "hooks-test-orderbook-level-row-odd"
                        }`}
                        onClick={() =>
                          handleItemClick({ raw: bid }, "Orderbook Bid Level")
                        }
                      >
                        <span>{bid.price.toFixed(8)}</span>
                        <span>{bid.size.toFixed(8)}</span>
                        <span>{bid.cumulative.toFixed(8)}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <h3 className="hooks-test-orderbook-section-title hooks-test-orderbook-section-title-asks">
                  Asks ({orderbookAsks.length})
                </h3>
                <div className="hooks-test-orderbook-levels-container">
                  {orderbookAsks
                    .slice(0, MAX_ORDERBOOK_LEVELS_DISPLAY)
                    .map((ask, idx) => (
                      <div
                        key={idx}
                        className={`hooks-test-orderbook-level-row ${
                          idx % 2 === 0
                            ? "hooks-test-orderbook-level-row-even"
                            : "hooks-test-orderbook-level-row-odd"
                        }`}
                        onClick={() =>
                          handleItemClick({ raw: ask }, "Orderbook Ask Level")
                        }
                      >
                        <span>{ask.price.toFixed(8)}</span>
                        <span>{ask.size.toFixed(8)}</span>
                        <span>{ask.cumulative.toFixed(8)}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            {orderbookSpread !== undefined && (
              <div className="hooks-test-orderbook-spread-info">
                <strong>Spread:</strong> {orderbookSpread.toFixed(8)} |{" "}
                <strong>Mid Price:</strong>{" "}
                {orderbookMidPrice?.toFixed(8) || "N/A"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rates Section (Funding Rate, Deviation, TWAP) */}
      <div className="App-section">
        <SectionHeader
          title="Rates Hook (Funding Rate, Deviation, TWAP)"
          isConnected={fundingRateConnected}
          statusBadges={[]}
          actions={[]}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (controlled by Market Selection above):
              <input
                type="text"
                value={fundingRateMarketId}
                onChange={(e) => {
                  const id = e.target.value.trim();
                  setFundingRateMarketId(id || "0");
                }}
                placeholder="0"
                className={`hooks-test-input ${
                  selectedMarketId
                    ? "hooks-test-input-controlled"
                    : "hooks-test-input-uncontrolled"
                }`}
              />
              <div className="hooks-test-helper-text">
                Subscribe to a single market. Can be manually overridden if
                needed.
              </div>
            </label>
          </div>
        </div>

        {fundingRateError && (
          <div className="hooks-test-error-box">Error: {fundingRateError}</div>
        )}

        {fundingRateLoading && !formattedRates ? (
          <div className="hooks-test-loading-state">
            Loading rates...
          </div>
        ) : !formattedRates ? (
          <div className="hooks-test-empty-state">
            No rates data available
          </div>
        ) : (
          <div className="hooks-test-funding-rate-list">
            {/* Funding Rate Card */}
            {formattedRates.fundingRate && (
              <div
                className="hooks-test-funding-rate-card"
                onClick={() =>
                  handleItemClick(
                    { data: formattedRates.fundingRate!.latest },
                    `Funding Rate - Market ${formattedRates.marketId}`,
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleItemClick(
                      { data: formattedRates.fundingRate!.latest },
                      `Funding Rate - Market ${formattedRates.marketId}`,
                    );
                  }
                }}
                aria-label={`View funding rate details for market ${formattedRates.marketId}`}
              >
                <div className="hooks-test-funding-rate-grid">
                  <div>
                    <strong>Market ID:</strong> {formattedRates.marketId}
                  </div>
                  <div>
                    <strong>Type:</strong> Funding Rate
                  </div>
                  <div>
                    <strong>Symbol:</strong> {formattedRates.fundingRate.symbol}
                  </div>
                  <div>
                    <strong>Funding Rate:</strong>{" "}
                    {formattedRates.fundingRate.fundingRate}
                  </div>
                  <div>
                    <strong>Mark Price:</strong>{" "}
                    {formattedRates.fundingRate.markPrice}
                  </div>
                  <div>
                    <strong>Index Price:</strong>{" "}
                    {formattedRates.fundingRate.indexPrice}
                  </div>
                  <div>
                    <strong>Premium Rate:</strong>{" "}
                    {formattedRates.fundingRate.premiumRate}
                  </div>
                  {formattedRates.fundingRate.formattedTimestamp && (
                    <div>
                      <strong>Timestamp:</strong>{" "}
                      {formattedRates.fundingRate.formattedTimestamp}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Deviation Card */}
            {formattedRates.deviation && (
              <div
                className="hooks-test-funding-rate-card"
                onClick={() =>
                  handleItemClick(
                    { data: formattedRates.deviation!.latest },
                    `Deviation - Market ${formattedRates.marketId}`,
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleItemClick(
                      { data: formattedRates.deviation!.latest },
                      `Deviation - Market ${formattedRates.marketId}`,
                    );
                  }
                }}
                aria-label={`View deviation details for market ${formattedRates.marketId}`}
              >
                <div className="hooks-test-funding-rate-grid">
                  <div>
                    <strong>Market ID:</strong> {formattedRates.marketId}
                  </div>
                  <div>
                    <strong>Type:</strong> Deviation
                  </div>
                  <div>
                    <strong>Symbol:</strong> {formattedRates.deviation.symbol}
                  </div>
                  <div>
                    <strong>Deviation (BPS):</strong>{" "}
                    {formattedRates.deviation.deviationBps}
                  </div>
                  <div>
                    <strong>Deviation (%):</strong>{" "}
                    {formattedRates.deviation.deviationPercent}
                  </div>
                  <div>
                    <strong>Last Price:</strong>{" "}
                    {formattedRates.deviation.lastPrice}
                  </div>
                  <div>
                    <strong>Mark Price:</strong>{" "}
                    {formattedRates.deviation.markPrice}
                  </div>
                  <div>
                    <strong>Index Price:</strong>{" "}
                    {formattedRates.deviation.indexPrice}
                  </div>
                  <div>
                    <strong>Direction:</strong> {formattedRates.deviation.direction}
                  </div>
                  {formattedRates.deviation.formattedTimestamp && (
                    <div>
                      <strong>Timestamp:</strong>{" "}
                      {formattedRates.deviation.formattedTimestamp}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TWAP Card */}
            {formattedRates.twap && (
              <div
                className="hooks-test-funding-rate-card"
                onClick={() =>
                  handleItemClick(
                    { data: formattedRates.twap!.latest },
                    `TWAP - Market ${formattedRates.marketId}`,
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleItemClick(
                      { data: formattedRates.twap!.latest },
                      `TWAP - Market ${formattedRates.marketId}`,
                    );
                  }
                }}
                aria-label={`View TWAP details for market ${formattedRates.marketId}`}
              >
                <div className="hooks-test-funding-rate-grid">
                  <div>
                    <strong>Market ID:</strong> {formattedRates.marketId}
                  </div>
                  <div>
                    <strong>Type:</strong> TWAP
                  </div>
                  <div>
                    <strong>Symbol:</strong> {formattedRates.twap.symbol}
                  </div>
                  <div>
                    <strong>TWAP Value:</strong> {formattedRates.twap.twapValue}
                  </div>
                  <div>
                    <strong>Window Size:</strong> {formattedRates.twap.windowSize}
                  </div>
                  <div>
                    <strong>Sample Count:</strong>{" "}
                    {formattedRates.twap.sampleCount}
                  </div>
                  <div>
                    <strong>Calculation Method:</strong>{" "}
                    {formattedRates.twap.calculationMethod}
                  </div>
                  <div>
                    <strong>Start Time:</strong> {formattedRates.twap.startTime}
                  </div>
                  <div>
                    <strong>End Time:</strong> {formattedRates.twap.endTime}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Operations Section */}
      <OrderOperationSection
        isReady={tradeOpIsReady}
        loading={tradeOpLoading}
        result={tradeOpResult}
        selectedMarketId={selectedMarketId}
        buckets={buckets}
        markPrice={markPrice}
        lastPrice={lastPrice}
        orderPlacementMnemonic={orderPlacementMnemonic}
        setOrderPlacementMnemonic={setOrderPlacementMnemonic}
        orderPlacementBucketId={orderPlacementBucketId}
        setOrderPlacementBucketId={setOrderPlacementBucketId}
        orderPlacementSide={orderPlacementSide}
        setOrderPlacementSide={setOrderPlacementSide}
        orderPlacementOrderType={orderPlacementOrderType}
        setOrderPlacementOrderType={setOrderPlacementOrderType}
        orderPlacementPrice={orderPlacementPrice}
        setOrderPlacementPrice={setOrderPlacementPrice}
        orderPlacementQuantity={orderPlacementQuantity}
        setOrderPlacementQuantity={setOrderPlacementQuantity}
        orderPlacementLeverage={orderPlacementLeverage}
        setOrderPlacementLeverage={setOrderPlacementLeverage}
        handlePlaceOrder={handlePlaceOrder}
        handleRandomize={handleRandomize}
        getOrderPrice={getOrderPrice}
        calculateSize={calculateSize}
      />

      {/* Multi-Feed Section */}
      <div className="App-section">
        <SectionHeader
          title="Multi-Feed Channel"
          isConnected={multiFeedConnected}
          statusBadges={[
            <span
              key="messages-count"
              className="hooks-test-status-badge hooks-test-status-badge-info"
            >
              {recentMessages.length + coloredMessages.length} messages
            </span>,
          ]}
          actions={[
            <button
              key="clear"
              className="hooks-test-button"
              onClick={() => {
                setRecentMessages([]);
                setColoredMessages([]);
                setMultiFeedSubChannelCounts(new Map());
              }}
            >
              Clear
            </button>,
          ]}
        />

        <div className="hooks-test-form-grid">
          <div>
            <label className="hooks-test-label">
              Market ID (from Market Selection):
              <input
                type="text"
                className="hooks-test-input-readonly"
                value={multiFeedMarketId}
                readOnly
                placeholder="Select market from dropdown above"
              />
            </label>
          </div>
          <div>
            <label className="hooks-test-label">
              Symbol:
              <input
                type="text"
                className="hooks-test-input-readonly"
                value={multiFeedSymbol}
                readOnly
                placeholder="BTC/USDC"
              />
              <div className="hooks-test-helper-text">
                Symbol is controlled by Trade Price Symbol above
              </div>
            </label>
          </div>
        </div>

        {/* Sub-Channel Statistics - Keep above tabs */}
        {multiFeedSubChannelCounts.size > 0 && (
          <div className="hooks-test-multifeed-stats-container">
            <div className="hooks-test-multifeed-badges-container">
              {Array.from(multiFeedSubChannelCounts.entries()).map(
                ([subChannel, count]) => (
                  <span
                    key={subChannel}
                    className="hooks-test-status-badge hooks-test-status-badge-info hooks-test-multifeed-badge-clickable"
                    onClick={() => {
                      const allMessages = [
                        ...recentMessages,
                        ...coloredMessages,
                      ];
                      const messages = allMessages
                        .filter((m) => m.sub_channel === subChannel)
                        .reverse();
                      if (messages.length > 0) {
                        handleItemClick(
                          { raw: messages },
                          `Multi-Feed: ${subChannel}`,
                        );
                      }
                    }}
                  >
                    {subChannel}: {count}
                  </span>
                ),
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="hooks-test-multifeed-tabs">
          <button
            className={`hooks-test-multifeed-tab ${
              activeMultiFeedTab === "recent" ? "active" : ""
            }`}
            onClick={() => setActiveMultiFeedTab("recent")}
          >
            Recent Messages (Last 20)
          </button>
          <button
            className={`hooks-test-multifeed-tab ${
              activeMultiFeedTab === "colored" ? "active" : ""
            }`}
            onClick={() => setActiveMultiFeedTab("colored")}
          >
            Colored Messages (Last 100)
          </button>
        </div>

        {/* Tab Content */}
        {activeMultiFeedTab === "recent" ? (
          <MultiFeedMessagesList
            messages={[...recentMessages].reverse()}
            maxDisplay={20}
            onMessageClick={handleItemClick}
          />
        ) : (
          <MultiFeedMessagesList
            messages={[...coloredMessages].reverse()}
            maxDisplay={100}
            onMessageClick={handleItemClick}
          />
        )}
      </div>

      {/* Data Modal */}
      <DataModal
        data={modalData}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={modalTitle}
      />
    </div>
  );
}

/**
 * Wrapper component that provides SubscriptionContext
 */
export default function HooksTestPage() {
  return (
    <SubscriptionProvider>
      <HooksTestPageContent />
    </SubscriptionProvider>
  );
}
